import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import { Box, LinearProgress } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { useEffect, useMemo, useState } from 'react';
import { getMarkingPeriods, setCurrentMarkingPeriodId } from 'store/lookup';
import { ALL_MARKING_PERIODS_ID, resolveCurrentMarkingPeriodId } from 'utils';

const MarkingPeriodField = ({ label = 'Marking Period', includeAll = false, paddingTop = 2, academicYearId, ...props }) => {
  const dispatch = useDispatch();
  const markingPeriods = useSelector(state => state.lookup.markingPeriods)?.markingPeriods;
  const currentMarkingPeriodId = useSelector(state => state.lookup.currentMarkingPeriodId);
  const { width = '100%', size = 'medium' } = props;

  useEffect(() => {
    if (!markingPeriods?.length) {
      dispatch(getMarkingPeriods({ id: academicYearId }));
    }
  }, [markingPeriods?.length, dispatch, academicYearId]);

  const [picked, setPicked] = useState(false);

  useEffect(() => {
    if (!markingPeriods?.length || picked) return;

    const defaultId = includeAll ? ALL_MARKING_PERIODS_ID : resolveCurrentMarkingPeriodId(markingPeriods);
    if (currentMarkingPeriodId !== defaultId) dispatch(setCurrentMarkingPeriodId(defaultId));
  }, [dispatch, markingPeriods, includeAll, picked, currentMarkingPeriodId]);

  const options = useMemo(
    () => (includeAll ? [{ id: ALL_MARKING_PERIODS_ID, name: 'All' }, ...(markingPeriods ?? [])] : markingPeriods ?? []),
    [markingPeriods, includeAll]
  );

  const selectedOption = useMemo(
    () => options?.find(opt => opt.id === currentMarkingPeriodId) || null,
    [options, currentMarkingPeriodId]
  );

  if (!markingPeriods?.length) return <LinearProgress color="primary" />;

  return (
    <Box pt={paddingTop} px={2} sx={{ width }}>
      <Autocomplete
        options={options}
        value={selectedOption}
        getOptionLabel={option => option?.name ?? ''}
        isOptionEqualToValue={(option, val) => option.id === val.id}
        onChange={(_, newValue) => {
          setPicked(true);
          dispatch(setCurrentMarkingPeriodId(newValue?.id ?? null));
        }}
        renderInput={params => <TextField {...params} label={label} variant="outlined" />}
        disableClearable
        {...props}
        size={size}
      />
    </Box>
  );
};

export default MarkingPeriodField;
